import { toAscii } from "./tokenizer";
import { EngineGuardStatus, ExtractedEntities, GuardResult } from "../types";
import { isIncident } from "../../utils/incidentParser";

const MOBBING_KEYWORDS: Record<string, string[]> = {
  PERSONAL_ATTACKS: [
    "debil", "debilem", "idiota", "idiotką", "głupi", "głup", "głąb", "kretyn", "imbcyl",
    "matole", "gamoń", "frajer", "patałach", "nieudacznik", "beznadziejny",
    "bezwartościowy", "nikczemny", "śmieć", "świnia", "suka", "kurwa", "chuj", "pojeb",
    "pojeba", "skurwysyn", "dziwka", "cipa", "dziebko"
  ],
  HUMILIATION: [
    "cały zespół ma dość", "wszyscy się z niego śmieją", "nie nadaje się",
    "tragedia", "dramat", "pomyłka przy rekrutacji", "kompletna porażka",
    "wstyd", "żenada", "kompromitacja", "fatalny", "marny", "beznadziejny"
  ],
  THREATS: [
    "pozbyć się", "pozbędziemy się", "wywalić", "wywalimy", "wyrzucić", "wyrzucimy",
    "zwolnić", "zwolnię", "zwolnimy", "będzie miał przesrane", "pogrążymy",
    "zniszczyć", "zgładzić", "ukarać", "zawiesić", "wydalić"
  ],
  GROUP_HARASSMENT: [
    "wszyscy przeciwko", "izolujemy", "nikt go nie lubi", "wszyscy mają dość",
    "wykluczyć", "odizolować", "bojkot", "gnębić", "prześladować", "napastować"
  ],
  HATE_SPEECH: [
    "czarnuch", "pedał", "żydzie", "islamski", "ciota", "bialas", "polaczek",
    "bambus", "murzyn", "rasista", "homofob", "ksenofob", "dyskrymin", "seksista",
    "gej"
  ]
};

const PRIVACY_KEYWORDS = [
  "depresja", "depresji", "depresję", "depresją",
  "choroba", "choruje", "chory", "chora",
  "lekarz", "lekarza", "lekarzem", "hospitalizacja", "hospitalizowany",
  "adres", "adresu", "adresie",
  "telefon", "telefonu", "numer komórkowy", "numer telefonu",
  "email", "e-mail", "poczta",
  "pesel", "peselu",
  "prywatny", "prywatna", "intymny", "intymna",
  "zdrowie", "zaburzenie", "psychiatra", "terapeuta",
  "wiek", "data urodzenia", "urodziny",
  "dziecko", "dzieci", "mąż", "żona", "rodzina"
];

const NOT_BUSINESS_KEYWORDS = [
  "kupić", "kupię", "kupować",
  "mleko", "sklep", "zakupy", "zakupić",
  "pogoda", "obiad", "kolacja", "śniadanie",
  "samochód", "auto", "dom", "mieszkanie",
  "rodzina", "dziecko", "dzieci", "wnuki",
  "urlop", "wakacje", "wolne",
  "impreza", "spotkanie prywatne", "kino", "restauracja", "pub"
];

const WORK_WORDS = [
  // Rdzenie zamiast pojedynczych form odmiany — "rozmowa" nie łapało "porozmawiać",
  // "praca" nie łapało "pracy" (dokładne dopasowanie substring, nie fleksja).
  "rozmow", "rozmaw", "ustal",
  "awari", "usterk", "wyciek", "ciek", "suwnic", "prostownik",
  "wann", "kąpiel", "bęben", "lini", "detal", "zawieszk",
  "maszyn", "prac", "hal", "produkcj", "operator", "kierownik",
  "pracownik", "zrobi", "zamówi", "sprawdzi", "napraw", "wymieni", "zmian",
  // Sygnały rozmowy dyscyplinującej/oceniającej pracownika (ścieżka A) — bez nich
  // taka legalna, robocza rozmowa (bez wzmianki o maszynie) była odrzucana jako
  // NOT_BUSINESS/NO_WORK_CONTEXT.
  "spóźni", "nieobecnoś", "absencj", "nie stawił", "nie stawiła",
  "polecen", "zlecen", "obowiązk", "lekceważ", "ignoruj",
  "niesubordynacj", "upomnien", "nagan", "dyscyplinując",
  "zachowani", "postaw", "wydajnoś", "zaangażowani",
];

function normalizeGuardText(text: string): string {
  return toAscii(text).toLowerCase().replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
}

function containsAny(text: string, phrases: string[]): boolean {
  const n = normalizeGuardText(text);
  for (const p of phrases) {
    if (n.includes(normalizeGuardText(p))) return true;
  }
  return false;
}

function scanMobbing(text: string): { status: EngineGuardStatus; reason: string } | null {
  const n = normalizeGuardText(text);
  for (const [reason, phrases] of Object.entries(MOBBING_KEYWORDS)) {
    for (const phrase of phrases) {
      if (n.includes(normalizeGuardText(phrase))) {
        return { status: "MOBBING_WARNING", reason };
      }
    }
  }
  return null;
}

function hasWorkContext(text: string, entities?: ExtractedEntities): boolean {
  if (entities) {
    if (entities.employee || entities.equipment || entities.location || entities.failure || entities.arrangement) {
      return true;
    }
  }
  const n = normalizeGuardText(text);
  for (const w of WORK_WORDS) {
    // Uwaga: lista WORK_WORDS zawiera polskie znaki diakrytyczne ("ustaliliśmy"),
    // a `n` jest już sprowadzone do ASCII — bez znormalizowania też `w` porównanie
    // NIGDY by nie trafiło (realny bug: legalne rozmowy o pracowniku bez słowa
    // maszynowego były odrzucane jako NOT_BUSINESS/NO_WORK_CONTEXT).
    if (n.includes(normalizeGuardText(w))) return true;
  }
  // Dodatkowy, szerszy sygnał: słownik kategorii zgłoszeń (Awaria/BHP/Magazyn)
  // z incidentParser.ts jest dużo bogatszy niż WORK_WORDS. Realny przykład, który
  // bez tego był fałszywie odrzucany: rozpoznawanie mowy w hałasie zniekształciło
  // "hala"/"sekcja"/"ładowarka" do niezrozumiałych słów, ale "serwis"/"pilnie"
  // przetrwały — isIncident() to złapie, WORK_WORDS nie.
  if (isIncident(text)) return true;
  return false;
}

function hasPrivacyData(text: string): { found: boolean; reason: string } | null {
  const n = normalizeGuardText(text);
  for (const k of PRIVACY_KEYWORDS) {
    if (n.includes(normalizeGuardText(k))) return { found: true, reason: `PRIVACY_KEYWORD: ${k}` };
  }

  if (/\b\d{11}\b/.test(text)) {
    return { found: true, reason: "PESEL" };
  }
  if (/(?:\+?\d{1,3}[-.\s]?)?\d{3}[-.\s]?\d{3}[-.\s]?\d{3}/.test(text)) {
    return { found: true, reason: "PHONE_NUMBER" };
  }
  if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/.test(text)) {
    return { found: true, reason: "EMAIL" };
  }
  if (/\b(ul\.?|ulica|al\.?|aleja|pl\.?|plac)\s+[a-ząćęłńóśźż]+/i.test(text)) {
    return { found: true, reason: "ADDRESS" };
  }

  return null;
}

export function checkGuard(text: string, entities?: ExtractedEntities): GuardResult {
  const mob = scanMobbing(text);
  if (mob) {
    return {
      status: mob.status,
      warningText: "Wykryto treści mobbingowe. Sformułuj notatkę w inny sposób.",
      reason: mob.reason
    };
  }

  const privacy = hasPrivacyData(text);
  if (privacy) {
    return {
      status: "PRIVACY_WARNING",
      warningText: "Notatka zawiera dane wrażliwe. Usuń informacje medyczne/prywatne.",
      reason: privacy.reason
    };
  }

  const hasWork = hasWorkContext(text, entities);
  const isNotBusiness = containsAny(text, NOT_BUSINESS_KEYWORDS);

  if (!hasWork && isNotBusiness) {
    return {
      status: "NOT_BUSINESS",
      warningText: "Notatka nie dotyczy pracy. Nagrywanie anulowane.",
      reason: "NOT_BUSINESS_KEYWORDS"
    };
  }

  if (!hasWork) {
    return {
      status: "NOT_BUSINESS",
      warningText: "Notatka nie dotyczy pracy. Nagrywanie anulowane.",
      reason: "NO_WORK_CONTEXT"
    };
  }

  return { status: "OK", warningText: "" };
}
